import { array, hash } from "@ember/helper";
import { LinkTo } from "@ember/routing";
import { trustHTML } from "@ember/template";
import CategoryListItem from "discourse/components/category-list-item";
import CategoryTitleLink from "discourse/components/category-title-link";
import CategoryUnread from "discourse/components/category-unread";
import MobileCategoryTopic from "discourse/components/mobile-category-topic";
import PluginOutlet from "discourse/components/plugin-outlet";
import SubCategoryItem from "discourse/components/sub-category-item";
import SubCategoryRow from "discourse/components/sub-category-row";
import FeaturedTopic from "discourse/components/topic-list/featured-topic";
import borderColor from "discourse/helpers/border-color";
import categoryColorVariable from "discourse/helpers/category-color-variable";
import lazyHash from "discourse/helpers/lazy-hash";
import { gt } from "discourse/truth-helpers";
import DDecoratedHtml from "discourse/ui-kit/d-decorated-html";
import dDirSpan from "discourse/ui-kit/helpers/d-dir-span";
import { i18n } from "discourse-i18n";

export default class ParentCategoryRow extends CategoryListItem {
  get hiddenSubcategoryCount() {
    return (
      (this.category.subcategory_count ?? 0) -
      this.displayedSubcategories.length
    );
  }

  <template>
    {{#unless this.isHidden}}
      <PluginOutlet
        @name="category-list-above-each-category"
        @outletArgs={{lazyHash category=this.category}}
      />

      {{#if this.site.mobileView}}
        <PluginOutlet
          @name="category-list-before-category-mobile"
          @outletArgs={{lazyHash
            category=this.category
            listType=this.listType
            isMuted=this.isMuted
          }}
        />
        <div
          class="category-list-item category {{if this.isMuted 'muted'}}"
          data-category-id={{this.category.id}}
          data-notification-level={{this.category.notificationLevelString}}
          style={{borderColor this.category.color}}
        >
          <table class="topic-list">
            <tbody>
              <tr>
                <th class="main-link">
                  <CategoryTitleLink @category={{this.category}} />
                </th>
                <PluginOutlet
                  @name="category-list-after-title-mobile-section"
                  @outletArgs={{lazyHash category=this.category}}
                />
              </tr>
              {{#if this.category.description_excerpt}}
                <tr class="category-description">
                  <td colspan="3">
                    <DDecoratedHtml
                      @html={{dDirSpan
                        this.category.description_excerpt
                        htmlSafe="true"
                      }}
                    />
                  </td>
                </tr>
              {{/if}}
              {{#unless this.isMuted}}
                {{#if this.showTopics}}
                  {{#each this.category.featuredTopics as |t|}}
                    <MobileCategoryTopic @topic={{t}} />
                  {{/each}}
                {{/if}}
              {{/unless}}
              {{#if this.showsGrandchildren}}
                {{#each this.displayedSubcategories as |subcategory|}}
                  <SubCategoryRow
                    @category={{subcategory}}
                    @listType={{this.listType}}
                  />
                {{/each}}
              {{else if this.displayedSubcategories}}
                <tr class="subcategories-list">
                  <td>
                    <div class="subcategories">
                      {{#each this.displayedSubcategories as |subcategory|}}
                        <SubCategoryItem
                          @category={{subcategory}}
                          @listType={{this.listType}}
                        />
                      {{/each}}
                    </div>
                  </td>
                </tr>
              {{/if}}
            </tbody>
          </table>
          <footer class="clearfix category-topics-count">
            <div class="category-stat">
              <a href={{this.category.url}}>
                {{trustHTML this.category.statTotal}}
              </a>
            </div>
            {{#unless this.category.pickAll}}
              <div class="category-stat">
                <a href={{this.category.url}}>
                  {{trustHTML this.category.stat}}
                </a>
              </div>
            {{/unless}}
          </footer>
        </div>
      {{else}}

        <tr
          class="{{if
              this.category.description_excerpt
              'has-description'
              'no-description'
            }}
            {{this.applyValueTransformer
              'parent-category-row-class'
              (array)
              (hash category=this.category)
            }}
            {{if this.category.uploaded_logo.url 'has-logo' 'no-logo'}}"
          data-category-id={{this.category.id}}
          data-notification-level={{this.category.notificationLevelString}}
        >

          <PluginOutlet
            @name="category-list-before-category-section"
            @outletArgs={{lazyHash
              category=this.category
              listType=this.listType
            }}
          />

          <td
            class="category {{if this.isMuted 'muted'}}"
            style={{categoryColorVariable this.category.color}}
          >
            <CategoryTitleLink @category={{this.category}} />
            <PluginOutlet
              @connectorTagName="div"
              @name="below-category-title-link"
              @outletArgs={{lazyHash category=this.category}}
            />

            {{#if this.category.description_excerpt}}
              <div class="category-description">
                <DDecoratedHtml
                  @html={{dDirSpan
                    this.category.description_excerpt
                    htmlSafe="true"
                  }}
                />
              </div>
            {{/if}}

            {{#if this.showsGrandchildren}}
              <table class="category-list subcategories-with-subcategories">
                <tbody>
                  {{#each this.displayedSubcategories as |subcategory|}}
                    <SubCategoryRow
                      @category={{subcategory}}
                      @listType={{this.listType}}
                    />
                  {{/each}}
                  {{#if (gt this.hiddenSubcategoryCount 0)}}
                    {{i18n
                      "category_row.subcategory_count"
                      count=this.hiddenSubcategoryCount
                    }}
                  {{/if}}
                </tbody>
              </table>
            {{else if this.displayedSubcategories}}
              <div class="subcategories">
                {{#each this.displayedSubcategories as |subcategory|}}
                  <SubCategoryItem
                    @category={{subcategory}}
                    @listType={{this.listType}}
                  />
                {{/each}}
                {{#if (gt this.hiddenSubcategoryCount 0)}}
                  <div class="subcategories__more-subcategories">
                    <LinkTo
                      @model={{this.slugPath}}
                      @route="discovery.subcategories"
                    >
                      {{i18n
                        "category_row.subcategory_count"
                        count=this.hiddenSubcategoryCount
                      }}
                    </LinkTo>
                  </div>
                {{/if}}
              </div>
            {{/if}}
          </td>

          <PluginOutlet
            @name="category-list-before-topics-section"
            @outletArgs={{lazyHash category=this.category}}
          />

          <PluginOutlet
            @name="category-list-topics-wrapper"
            @outletArgs={{lazyHash category=this.category}}
          >
            <td class="topics topic-list-data num">
              <div title={{this.category.statTitle}}>{{trustHTML
                  this.category.stat
                }}</div>
              <CategoryUnread
                class="unread-new"
                @category={{this.category}}
                @newTopicsCount={{this.newTopicsCount}}
                @tagName="div"
                @unreadTopicsCount={{this.unreadTopicsCount}}
              />
            </td>
          </PluginOutlet>

          <PluginOutlet
            @name="category-list-after-topics-section"
            @outletArgs={{lazyHash category=this.category}}
          />

          <PluginOutlet
            @name="category-list-latest-wrapper"
            @outletArgs={{lazyHash
              category=this.category
              showTopics=this.showTopics
            }}
          >
            {{#unless this.isMuted}}
              {{#if this.showTopics}}
                <td class="latest">
                  {{#each this.category.featuredTopics as |t|}}
                    <FeaturedTopic @topic={{t}} />
                  {{/each}}
                </td>
                <PluginOutlet
                  @name="category-list-after-latest-section"
                  @outletArgs={{lazyHash category=this.category}}
                />
              {{/if}}
            {{/unless}}
          </PluginOutlet>
        </tr>
      {{/if}}
    {{/unless}}
  </template>
}
